// Paleta fixa de cores de agrupamento para os nos do diagrama de equipamentos, mais o calculo de
// contraste que escolhe o shade certo por modo — ver spec
// .artifacts-ai/specs/2026-09-04-cor-nos-equipamentos-diagrama-design.md para o raciocinio
// completo. theme.palette.colors nao varia por modo claro/escuro, entao um shade fixo (ex:
// sempre "dark") fica ilegivel num dos dois modos para a maioria das cores.

// Chaves de cor validas para agrupar equipamentos (exclui black/white, que nao servem como
// categoria visual)
export const NODE_COLOR_KEYS = [
  "blue",
  "green",
  "red",
  "orange",
  "yellow",
  "purple",
  "pink",
  "gray",
];

// Ordem de tentativa de shade por modo. Claro tenta "medium" primeiro — é o tom mais saturado/
// puro de cada cor (ex: verde de verdade, azul de verdade), o que mantém cores diferentes
// visualmente distintas entre si; "dark"/"darker" tendem a convergir pra um mesmo cinza-escuro-
// azulado conforme escurecem (ex: verde e azul escurecidos ficavam parecidos), então só entram
// como fallback de contraste pras cores cujo "medium" não atinge o mínimo contra fundo branco
// (laranja, amarelo, cinza). Escuro tenta o inverso ("light" primeiro): os tons claros já são o
// suficiente vívidos/distintos entre si contra fundo escuro, sem precisar desse ajuste
const SHADE_ORDER_LIGHT = ["medium", "dark", "darker", "light", "lighter"];
const SHADE_ORDER_DARK = ["light", "lighter", "medium", "dark", "darker"];

// Contraste minimo exigido (WCAG 1.4.11, limite grafico de componente de UI)
const MIN_CONTRAST = 3;

// Opacidade do tingimento de fundo do card — pre-misturada em hex solido (ver blendHex), nao
// rgba real, pra o resultado nao depender do que estiver atras do card no canvas
const TINT_ALPHA = 0.1;

const hexToRgb = (hex) => {
  const limpo = (hex || "").replace("#", "");
  const bigint = parseInt(limpo, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
};

const canalParaLinear = (canal) => {
  const c = canal / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

const luminanciaRelativa = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  return (
    0.2126 * canalParaLinear(r) +
    0.7152 * canalParaLinear(g) +
    0.0722 * canalParaLinear(b)
  );
};

// Razao de contraste WCAG entre duas cores hex (ordem dos argumentos nao importa)
export const getContrastRatio = (hexA, hexB) => {
  const l1 = luminanciaRelativa(hexA);
  const l2 = luminanciaRelativa(hexB);
  const claro = Math.max(l1, l2);
  const escuro = Math.min(l1, l2);
  return (claro + 0.05) / (escuro + 0.05);
};

// Mistura hexFg sobre hexBg na opacidade alphaFrac (0-1), devolvendo um hex solido — usado pro
// tingimento de fundo do card nao depender de transparencia real (o que ficaria inconsistente
// dependendo do que estiver atras do card no canvas)
const blendHex = (hexFg, hexBg, alphaFrac) => {
  const fg = hexToRgb(hexFg);
  const bg = hexToRgb(hexBg);
  const paraHex = (n) => Math.round(n).toString(16).padStart(2, "0");
  const r = fg.r * alphaFrac + bg.r * (1 - alphaFrac);
  const g = fg.g * alphaFrac + bg.g * (1 - alphaFrac);
  const b = fg.b * alphaFrac + bg.b * (1 - alphaFrac);
  return `#${paraHex(r)}${paraHex(g)}${paraHex(b)}`;
};

// Escolhe, entre os 5 shades de uma cor do tema, o primeiro que atinge contraste >= 3:1 contra um
// fundo de referência (background.paper por padrão — o card do nó; passe bgOverride pra checar
// contra outro fundo, ex: o canvas do diagrama, usado pelas edges coloridas) no modo atual do
// tema. Se nenhum atingir (limitacao dos hex configurados pelo tenant pra aquela cor), usa o de
// maior contraste encontrado — nunca falha, so degrada. Devolve a mesma cor escolhida pra borda
// (hex puro) e pro tingimento de fundo (ja pre-misturado com o fundo de referência, pronto pra
// virar backgroundColor direto)
export const getAccessibleNodeColor = (corKey, theme, bgOverride) => {
  const shades = theme?.palette?.colors?.[corKey];
  const paper = bgOverride || theme?.palette?.background?.paper;
  if (!corKey || !shades || !paper) return null;

  const ordem =
    theme?.palette?.mode === "dark" ? SHADE_ORDER_DARK : SHADE_ORDER_LIGHT;

  let melhor = null;
  for (const shade of ordem) {
    const hex = shades[shade];
    if (!hex) continue;
    const contraste = getContrastRatio(hex, paper);
    if (!melhor || contraste > melhor.contraste) melhor = { hex, contraste };
    if (contraste >= MIN_CONTRAST) {
      return { border: hex, tint: blendHex(hex, paper, TINT_ALPHA) };
    }
  }
  if (!melhor) return null;
  return { border: melhor.hex, tint: blendHex(melhor.hex, paper, TINT_ALPHA) };
};

// Escolhe, entre branco e preto do tema, a cor de texto com melhor contraste sobre um fundo
// solido — usado pelos chips de status (SNMP/PING) do diagrama, cujo fundo vem de
// theme.palette.colors e varia por tenant, então não dá pra fixar uma cor de texto só.
// Mesma filosofia de getAccessibleNodeColor: nunca falha, sempre devolve a melhor opção
// disponível mesmo que nenhuma das duas atinja o mínimo de contraste pra texto (WCAG 1.4.3, 4.5:1)
export const getAccessibleTextColor = (hexBg, theme) => {
  const branco = theme?.palette?.colors?.white?.lighter;
  const preto = theme?.palette?.colors?.black?.medium;
  if (!hexBg || !branco || !preto) return theme?.palette?.text?.primary;

  return getContrastRatio(hexBg, branco) >= getContrastRatio(hexBg, preto)
    ? branco
    : preto;
};
